import {DateTime} from 'luxon';
import {z} from 'zod';
import {Sorting} from '@brunata-angular/ui';
import {
    caseInsensitiveComparator,
    composeComparators,
    keyComparator,
    numericLocaleComparator,
    zDateTimeAsSent,
    zNumberToString,
} from '@brunata-angular/util';
import {LocationsConsumer} from '@shared/services/location.service';
import {lastDateWithConsumption} from '../consumption-overview/consumption-overview.model';

const PULSE_COLLECTOR_ALLOCATION_UNIT = 'P';

export const meterWithReadingSchema = z.object({
    meter: z.object({
        meterId: zNumberToString,
        placement: z.string().nullable(),
        meterNo: z.string(),
        allocationUnit: z.string(),
        unit: z.string(),
        meterSequenceNo: z.number(),
    }),
    // The endpoint sends a reading object with null fields, not a null reading, for a meter without one.
    reading: z
        .object({
            readingDate: zDateTimeAsSent.nullable(),
            value: z.number().nullable(),
        })
        .nullable()
        .transform(reading =>
            reading?.readingDate == null || reading.value == null
                ? null
                : {readingDate: reading.readingDate, value: reading.value},
        ),
});

export type MeterWithReading = z.infer<typeof meterWithReadingSchema>;

type MeterComparator = (first: MeterWithReading, second: MeterWithReading) => number;

const byMeterSequence: MeterComparator = keyComparator(row => row.meter.meterSequenceNo);

/**
 * The pulse collector is the radio box the other meters report through, not a meter the
 * administrator reads, so the React screen left it out and so does this one.
 */
export function prepareMeterRows(meters: readonly MeterWithReading[]): MeterWithReading[] {
    return meters
        .filter(({meter}) => meter.allocationUnit !== PULSE_COLLECTOR_ALLOCATION_UNIT)
        .sort(byMeterSequence);
}

/** The texts the allocation and unit cells show, so those columns sort the way they read. */
export type MeterLabels = {
    allocationUnit: (allocationUnit: string) => string;
    unit: (unit: string) => string;
};

/** Until a column is chosen the rows keep the meter sequence; rows that tie on a column keep it too. */
export function sortMeterRows(
    rows: readonly MeterWithReading[],
    sorting: Sorting | null,
    labels: MeterLabels,
): MeterWithReading[] {
    const columnComparator = sorting == null ? byMeterSequence : meterComparator(sorting, labels);
    return [...rows].sort(composeComparators([columnComparator, byMeterSequence]));
}

function meterComparator(sorting: Sorting, labels: MeterLabels): MeterComparator {
    const direction = sorting.direction;
    switch (sorting.column) {
        case 'placement':
            // A meter without a placement stays at the bottom in both directions.
            return composeComparators<MeterWithReading>([
                keyComparator(row => (row.meter.placement == null ? 1 : 0)),
                caseInsensitiveComparator(row => row.meter.placement ?? '', direction),
            ]);
        case 'meterNo':
            return numericLocaleComparator(row => row.meter.meterNo, direction);
        case 'allocationUnit':
            return caseInsensitiveComparator(
                row => labels.allocationUnit(row.meter.allocationUnit),
                direction,
            );
        case 'readingDate':
            return keyComparator(
                row => row.reading?.readingDate.toMillis() ?? null,
                direction,
                'last',
            );
        case 'readingValue':
            return keyComparator(row => row.reading?.value ?? null, direction, 'last');
        case 'unit':
            return caseInsensitiveComparator(row => labels.unit(row.meter.unit), direction);
        default:
            return byMeterSequence;
    }
}

/**
 * The endpoint lists the meters mounted at the location on the requested date, so asking for today
 * on behalf of a consumer who has moved out shows whatever hangs there now, not what they had.
 * Their default is the move-out date, the same anchor the consumption chart uses.
 */
export function defaultReadingDate(consumer: LocationsConsumer, now: DateTime): DateTime {
    return lastDateWithConsumption(null, consumer.occupationEndDate, now) ?? now;
}
